/**
 * Callout brand kit — the overlay the render path draws onto every frame, and the palette it
 * draws it with.
 *
 * Source of truth for UI tokens is web/src/index.css (@theme); this module is the copy the
 * render overlay uses when drawing on a Canvas, where CSS vars are unavailable.
 *
 * THE overlay engine is the canvas (implementation-plan.md §2.2): there is no drawtext and no
 * filter graph, so this file is the whole of the branding that appears in an exported clip.
 */
import type { OverlayConfig } from '../render/types';

export type HexColor = `#${string}`;

export interface BrandPalette {
  charcoal: HexColor;
  copper: HexColor;
  chalk: HexColor;
}

export const CALLOUT_PALETTE: BrandPalette = {
  charcoal: '#2D2A26',
  copper: '#C87A3A',
  chalk: '#F5F2EB',
};

/** Persisted per user/club branding (mirrors the BrandingKit D1 entity). */
export interface BrandingKit {
  id: string;
  ownerType: 'user' | 'club';
  ownerId: string;
  logoUrl?: string;
  primaryColor: HexColor;
  secondaryColor: HexColor;
  clubName?: string;
}

/** The branding used when the coach has no BrandingKit row yet: Callout's own colours. */
export const DEFAULT_BRANDING_KIT: Omit<BrandingKit, 'id' | 'ownerId'> = {
  ownerType: 'user',
  primaryColor: CALLOUT_PALETTE.charcoal,
  secondaryColor: CALLOUT_PALETTE.copper,
  clubName: undefined,
};

export interface CanvasSize {
  width: number;
  height: number;
}

/**
 * Overlay geometry as fractions of the frame, so one definition covers 360p and 1080p.
 * The title bar is the card's design: a band across the top, logo at its right edge.
 */
export const OVERLAY_LAYOUT = {
  /** Horizontal safe margin. */
  marginX: 0.035,
  /** Title bar height, as a fraction of frame height (the card's `w x 60` at 720p). */
  barHeight: 0.115,
  /** Minimum bar height in pixels, so the title stays legible on small frames. */
  barMinPx: 34,
  titleSizeRatio: 0.075,
  titleMinPx: 16,
  clubSizeRatio: 0.042,
  clubMinPx: 11,
  /** Logo height as a fraction of the bar height. */
  logoHeightRatio: 0.62,
} as const;

/** A logo already decoded and ready to draw (kept out of compositeFrame so it stays sync). */
export interface OverlayAssets {
  logo?: CanvasImageSource | null;
}

/**
 * The free-tier watermark (card §2): "Made with Callout" + a speech-bubble mark, bottom-right,
 * 5% margin from the edges, white at ~40% opacity with a soft shadow so it holds up over bright
 * footage. It is drawn ON the frame, after the title bar, on every exported frame.
 */
export const WATERMARK_LAYOUT = {
  marginFraction: 0.05,
  opacity: 0.4,
  text: 'Made with Callout',
  textSizeRatio: 0.028,
  textMinPx: 11,
  markSizeRatio: 0.034,
  markMinPx: 13,
  /** Gap between the bubble mark and the text, as a fraction of the mark's height. */
  gapRatio: 0.45,
} as const;

/**
 * Draws the watermark in the bottom-right corner. Kept separate from compositeFrame's overlay
 * drawing so the "is the export watermarked?" question has exactly one implementation, used by
 * both the single-clip renderer and the reel renderer.
 */
export function drawWatermark(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  size: CanvasSize,
): void {
  const { width, height } = size;
  const margin = Math.round(Math.min(width, height) * WATERMARK_LAYOUT.marginFraction);
  const textSize = Math.max(
    WATERMARK_LAYOUT.textMinPx,
    Math.round(Math.min(width, height) * WATERMARK_LAYOUT.textSizeRatio),
  );
  const markSize = Math.max(
    WATERMARK_LAYOUT.markMinPx,
    Math.round(Math.min(width, height) * WATERMARK_LAYOUT.markSizeRatio),
  );

  ctx.save();
  ctx.globalAlpha = WATERMARK_LAYOUT.opacity;
  ctx.font = `600 ${textSize}px ui-sans-serif, system-ui, sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
  ctx.shadowBlur = Math.max(2, Math.round(textSize * 0.4));
  ctx.shadowOffsetY = Math.max(1, Math.round(textSize * 0.08));

  const baseline = height - margin;
  const textWidth = ctx.measureText(WATERMARK_LAYOUT.text).width;
  const gap = Math.round(markSize * WATERMARK_LAYOUT.gapRatio);
  const markLeft = Math.round(width - margin - textWidth - gap - markSize);
  const markTop = Math.round(baseline - markSize);

  drawSpeechBubble(ctx, markLeft, markTop, markSize);

  ctx.fillStyle = '#ffffff';
  ctx.fillText(WATERMARK_LAYOUT.text, width - margin, baseline);

  ctx.restore();
}

/** The logo mark: a speech bubble with a tail, drawn as one filled path. */
function drawSpeechBubble(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
): void {
  const radius = Math.max(2, Math.round(size * 0.22));
  const tailHeight = Math.round(size * 0.28);
  const bodyHeight = size - tailHeight;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  roundedRectPath(ctx, x, y, size, bodyHeight, radius);
  // tail, bottom-left of the bubble
  ctx.moveTo(x + size * 0.26, y + bodyHeight - 1);
  ctx.lineTo(x + size * 0.18, y + size);
  ctx.lineTo(x + size * 0.5, y + bodyHeight - 1);
  ctx.closePath();
  ctx.fill();

  // two dots so it reads as a Callout bubble, not a blank box
  ctx.globalAlpha *= 0.75;
  ctx.fillStyle = '#2D2A26';
  const dot = Math.max(1.5, size * 0.1);
  for (const offset of [0.3, 0.55]) {
    ctx.beginPath();
    ctx.arc(x + size * offset, y + bodyHeight * 0.55, dot, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha /= 0.75;
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Turn a D1 BrandingKit (+ the clip's title) into the OverlayConfig the renderer consumes.
 * A coach with no kit gets Callout's own colours, which is what the free tier sees.
 */
export function brandingKitToOverlay(
  kit: BrandingKit | null | undefined,
  clip: { title: string },
  options: { watermark?: boolean } = {},
): OverlayConfig {
  const source = kit ?? { ...DEFAULT_BRANDING_KIT, id: 'default', ownerId: 'default' };
  return {
    title: clip.title,
    logoUrl: source.logoUrl,
    primaryColor: source.primaryColor,
    secondaryColor: source.secondaryColor,
    clubName: source.clubName,
    watermark: options.watermark === true,
  };
}

/**
 * Decode a brand logo for canvas compositing. Returns null on any failure — a missing logo must
 * degrade to "no logo", never to a failed render.
 */
export async function loadLogoImage(url: string): Promise<HTMLImageElement | null> {
  if (typeof Image === 'undefined') return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/**
 * THE compositor — every exported frame goes through this once (§2.2):
 *
 *   1. the decoded source frame, full-bleed;
 *   2. a title bar across the top in the club's primary colour, with a secondary-colour rule;
 *   3. the clip title, centred, in white;
 *   4. the club logo, right-aligned inside the bar;
 *   5. the club name, small, under the logo.
 *
 * Synchronous by design: the WebCodecs path calls it per frame with one VideoFrame in flight.
 */
export function compositeFrame(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  frame: CanvasImageSource,
  overlay: OverlayConfig,
  size: CanvasSize,
  assets: OverlayAssets = {},
): void {
  const { width, height } = size;
  const marginX = Math.round(width * OVERLAY_LAYOUT.marginX);
  const barHeight = Math.max(
    OVERLAY_LAYOUT.barMinPx,
    Math.round(height * OVERLAY_LAYOUT.barHeight),
  );
  const titleSize = Math.max(
    OVERLAY_LAYOUT.titleMinPx,
    Math.round(height * OVERLAY_LAYOUT.titleSizeRatio),
  );
  const clubSize = Math.max(
    OVERLAY_LAYOUT.clubMinPx,
    Math.round(height * OVERLAY_LAYOUT.clubSizeRatio),
  );

  ctx.save();

  // 1. the frame itself
  ctx.drawImage(frame, 0, 0, width, height);

  // 2. the title bar
  ctx.globalAlpha = 0.88;
  ctx.fillStyle = overlay.primaryColor;
  ctx.fillRect(0, 0, width, barHeight);
  ctx.globalAlpha = 1;

  // secondary accent rule along the bottom edge of the bar
  ctx.fillStyle = overlay.secondaryColor;
  ctx.fillRect(0, barHeight - Math.max(2, Math.round(barHeight * 0.06)), width, Math.max(2, Math.round(barHeight * 0.06)));

  const logoHeight = Math.round(barHeight * OVERLAY_LAYOUT.logoHeightRatio);
  let logoWidth = 0;
  if (assets.logo) {
    const source = assets.logo as { width?: number; height?: number; naturalWidth?: number; naturalHeight?: number };
    const naturalW = source.naturalWidth ?? source.width ?? logoHeight;
    const naturalH = source.naturalHeight ?? source.height ?? logoHeight;
    logoWidth = naturalH > 0 ? Math.round((naturalW / naturalH) * logoHeight) : logoHeight;
    ctx.drawImage(assets.logo, width - marginX - logoWidth, Math.round((barHeight - logoHeight) / 2), logoWidth, logoHeight);
  }

  // 3. the clip title — white, centred (card: `fillStyle #ffffff` + `fillText(title, w/2, 40)`)
  ctx.fillStyle = '#ffffff';
  ctx.font = `600 ${titleSize}px ui-sans-serif, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const logoGutter = logoWidth > 0 ? logoWidth + marginX : 0;
  const maxTitleWidth = Math.max(40, width - 2 * marginX - 2 * logoGutter);
  ctx.fillText(truncateToWidth(ctx, overlay.title, maxTitleWidth), width / 2, barHeight / 2, maxTitleWidth);

  // 4./5. club name under the logo, right-aligned. It sits over the footage (the bar is only
  // tall enough for the title), so it carries a soft shadow to stay legible on any frame.
  if (overlay.clubName) {
    ctx.fillStyle = overlay.secondaryColor;
    ctx.font = `500 ${clubSize}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = Math.max(2, Math.round(clubSize * 0.3));
    ctx.fillText(overlay.clubName, width - marginX, barHeight + Math.round(clubSize * 0.35));
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.restore();

  // 6. the free-tier watermark, bottom-right, drawn last so nothing covers it (card §2).
  if (overlay.watermark) drawWatermark(ctx, size);
}

/** Trims a title with an ellipsis so a long label never bleeds outside the bar. */
function truncateToWidth(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let candidate = text;
  while (candidate.length > 1 && ctx.measureText(`${candidate}…`).width > maxWidth) {
    candidate = candidate.slice(0, -1);
  }
  return `${candidate}…`;
}
